"use client"

import Link from "next/link"

import type {
  DirectoryFilterBucket,
  DirectoryFilterOption,
} from "@/features/hospital-directory/lib/directoryFilter"
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react"

// hydration 여부 감지. 서버 스냅샷은 false라 서버 HTML은 항상 비승격 폴백으로 나가고,
// 클라이언트에서 마운트된 뒤에만 true가 된다(effect에서 setState 하지 않는 표준 방법).
const subscribeToNothing = () => () => {}
const getEnhancedSnapshot = () => true
const getServerSnapshot = () => false

type DirectoryFilterSheetProps = {
  // 칩(트리거)에 보일 현재 값
  chipLabel: string
  chipIcon?: ReactNode
  // 시트 헤더 제목(시안: "지역" / "진료과")과 앵커 문맥용 보충 설명
  title: string
  contextLabel: string
  buckets: DirectoryFilterBucket[]
  // 시도 사이드바를 쓸지(통이 여럿) 단순 목록인지(통이 하나)
  withSidebar: boolean
  // 값이 골라진 칩인지(루트에서는 둘 다 미선택이라 회색으로 둔다)
  isSelected: boolean
}

// 선택/미선택을 색으로 구분한다. 박스 규격(패딩·테두리·타이포)은 같아서 칩 높이는 34px로 동일하다.
const chipSummaryClassName = (isSelected: boolean) =>
  `flex w-fit cursor-pointer list-none items-center gap-1 rounded-full border px-3 py-1.5 text-sm font-semibold [&::-webkit-details-marker]:hidden ${
    isSelected
      ? "border-primary-300 bg-primary-50 text-primary-600"
      : "border-gray-200 bg-white text-gray-700"
  }`

const ARROW_KEYS = ["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft", "Home", "End"]

// 포커스 트랩 대상: 실제로 탭 순서에 있고 화면에 잡히는 것만
const focusablesIn = (root: HTMLElement) =>
  [...root.querySelectorAll<HTMLElement>("a[href], button, [tabindex]")]
    .filter((node) => node.tabIndex >= 0 && node.getClientRects().length > 0)

const nextIndexFor = (key: string, current: number, length: number) => {
  if (key === "Home") return 0
  if (key === "End") return length - 1
  const forward = key === "ArrowDown" || key === "ArrowRight"
  return (current + (forward ? 1 : -1) + length) % length
}

/**
 * 시안의 지역/진료과 바텀시트. 핵심 제약은 "링크가 초기 HTML에 그대로 있어야 한다"이다
 * — 이 페이지군의 존재 이유가 크롤러 도달성이라 portal로 옮기거나 JS로만 만들 수 없다.
 * 그래서 링크는 언제나 이 컴포넌트가 직접 렌더하고(서버 HTML에 포함), 바텀시트는
 * hydration 이후에만 켜지는 표현 레이어다:
 *   - JS 없음: 칩 아래 펼쳐지는 disclosure 패널 + 평범한 링크 목록(시도별 전부 나열)
 *   - JS 있음: 모달 다이얼로그(포커스 트랩·Esc·포커스 복귀) + 시도 tablist + 단일선택 radiogroup
 * 역할(dialog/tab/radio)은 승격됐을 때만 덧씌운다 — 비승격 경로는 계속 링크여야 하고
 * 크롤러가 보는 앵커 집합도 그대로여야 한다.
 *
 * 배경 비활성화에 inert를 쓰지 않은 이유: 시트가 <main> 안(칩 옆)에 있어야 링크가 서버
 * HTML에 남는데, 조상에 inert를 걸면 그 자손인 시트까지 죽는다. 그래서 aria-modal +
 * 포커스 트랩으로 같은 효과를 낸다.
 */
const DirectoryFilterSheet = ({
  chipLabel,
  chipIcon,
  title,
  contextLabel,
  buckets,
  withSidebar,
  isSelected: isChipSelected,
}: DirectoryFilterSheetProps) => {
  const baseId = useId()
  const detailsRef = useRef<HTMLDetailsElement>(null)
  const summaryRef = useRef<HTMLElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  // 서버 렌더는 항상 비승격 상태다. 승격은 mount 이후에만 일어나므로 hydration 불일치가 없다.
  const isEnhanced = useSyncExternalStore(subscribeToNothing, getEnhancedSnapshot, getServerSnapshot)
  // 열림 상태는 <details>가 진실이다. 이벤트로만 state를 쌓으면 hydration 전에 열어 둔 칩이
  // 승격 뒤에도 isOpen=false로 남아 포커스 진입·스크롤 잠금이 통째로 건너뛰어진다.
  const subscribeToToggle = useCallback((onStoreChange: () => void) => {
    const element = detailsRef.current
    element?.addEventListener("toggle", onStoreChange)
    return () => element?.removeEventListener("toggle", onStoreChange)
  }, [])
  const getOpenSnapshot = useCallback(() => detailsRef.current?.open ?? false, [])
  const isOpen = useSyncExternalStore(subscribeToToggle, getOpenSnapshot, getServerSnapshot)
  // 현재 페이지에 해당하는 항목이 초기 선택이자 "초기화"가 되돌아갈 지점이다.
  // null = "현재 보고 있는 곳" 그대로.
  const currentBucketIndex = Math.max(0, buckets.findIndex((bucket) =>
    bucket.options.some((option) => option.isCurrent)))
  const [visibleBucket, setVisibleBucket] = useState(currentBucketIndex)
  const [selectedHref, setSelectedHref] = useState<string | null>(null)

  const isDialog = isEnhanced

  // 시트가 떠 있는 동안 뒤 배경이 스크롤되지 않게 한다
  useEffect(() => {
    if (!isDialog || !isOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = previous }
  }, [isDialog, isOpen])

  // 모달을 열면 포커스가 시트 안으로 들어가야 한다(W3C Modal Dialog).
  // 컨테이너 자체에 넣어 다이얼로그 이름부터 읽히게 하고, 이어서 Tab이 ✕ → 시도 → 목록으로 간다.
  useEffect(() => {
    if (!isDialog || !isOpen) return
    sheetRef.current?.focus()
  }, [isDialog, isOpen])

  const close = ({ restoreFocus = true } = {}) => {
    if (detailsRef.current) detailsRef.current.open = false
    if (restoreFocus) summaryRef.current?.focus()
  }

  // 단일 선택. 아무것도 안 고르면 "현재 보고 있는 곳"이 선택 상태다.
  const isOptionSelected = (option: DirectoryFilterOption) =>
    selectedHref === null ? option.isCurrent : option.href === selectedHref

  const selectOption = (option: DirectoryFilterOption) => setSelectedHref(option.href ?? null)

  // 항목은 진짜 <a href>다. JS가 있을 때만 이동을 가로채 "선택"으로 바꾸고,
  // 없으면 눌러서 그대로 이동한다(점진적 향상).
  const onSelect = (event: MouseEvent<HTMLElement>, option: DirectoryFilterOption) => {
    if (!isEnhanced) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
    event.preventDefault()
    selectOption(option)
  }

  // Esc로 닫고, Tab이 시트를 벗어나지 않게 순환시킨다
  const onSheetKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!isDialog) return
    if (event.key === "Escape") {
      event.preventDefault()
      close()
      return
    }
    if (event.key !== "Tab") return
    const sheet = sheetRef.current
    if (!sheet) return
    const focusables = focusablesIn(sheet)
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement
    if (event.shiftKey && (active === first || active === sheet)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  // 시도 사이드바: 세로 tablist roving focus(레포 선례 DirectoryTabPanels와 같은 방식)
  const onTabKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!ARROW_KEYS.includes(event.key)) return
    event.preventDefault()
    const next = nextIndexFor(event.key, index, buckets.length)
    setVisibleBucket(next)
    tabRefs.current[next]?.focus()
  }

  // radio 계약: 방향키는 이동과 선택을 함께 하고 Space는 선택한다
  const onRadioKeyDown = (event: ReactKeyboardEvent<HTMLElement>, option: DirectoryFilterOption) => {
    if (event.key === " " || event.key === "Spacebar") {
      event.preventDefault()
      selectOption(option)
      return
    }
    if (!ARROW_KEYS.includes(event.key)) return
    event.preventDefault()
    const group = event.currentTarget.closest('[role="radiogroup"]')
    const radios = [...(group?.querySelectorAll<HTMLElement>('[role="radio"]') ?? [])]
    const index = radios.indexOf(event.currentTarget)
    if (index < 0 || radios.length === 0) return
    const target = radios[nextIndexFor(event.key, index, radios.length)]
    target?.focus()
    target?.click()
  }

  // 화면에는 label만, 앵커 textContent에는 srBefore + label + srAfter 전체가 남는다.
  // (JSX가 줄바꿈 공백을 지우므로 문자열 사이에 군더더기 공백이 끼지 않는다)
  const optionText = (option: DirectoryFilterOption) => (
    <>
      {option.srBefore ? <span className="sr-only">{option.srBefore}</span> : null}
      {option.label}
      {option.srAfter ? <span className="sr-only">{option.srAfter}</span> : null}
    </>
  )

  const checkBox = (isSelected: boolean) => (
    <span
      aria-hidden
      className="size-4 shrink-0 rounded-sm border-2 bg-center bg-no-repeat"
      style={{
        borderColor: isSelected ? "#6B00D7" : "#E5E6EB",
        backgroundColor: isSelected ? "#6B00D7" : "transparent",
        backgroundImage: isSelected ? 'url("/icons/checked.svg")' : "none",
        backgroundSize: "14px 14px",
      }}
    />
  )

  const rowClassName = (isSelected: boolean) =>
    `flex items-center gap-2 px-4 py-3 text-sm ${
      isSelected ? "font-semibold text-[#1D1E20]" : "text-[#26262C]"
    }`

  const optionRow = (
    option: DirectoryFilterOption,
    optionIndex: number,
    isRovingStop: boolean,
  ) => {
    const isSelected = isEnhanced ? isOptionSelected(option) : option.isCurrent
    // 승격 시에만 radio 역할을 덧씌운다. 비승격에서는 그냥 링크다.
    const radioProps = isEnhanced
      ? {
        role: "radio",
        "aria-checked": isSelected,
        tabIndex: isRovingStop ? 0 : -1,
        onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => onRadioKeyDown(event, option),
      }
      : {}
    // radiogroup이 radio를 직접 소유하도록 중간 li는 접근성 트리에서 뺀다
    const itemProps = isEnhanced ? { role: "presentation" as const } : {}

    // href가 없으면 현재 위치를 알려주는 정적 행이다(앵커를 새로 만들지 않는다)
    if (!option.href) {
      return (
        <li key={`current-${option.label}`} {...itemProps}>
          <p
            {...radioProps}
            aria-current={isEnhanced ? undefined : "true"}
            data-selected={isSelected ? "true" : "false"}
            onClick={(event) => onSelect(event, option)}
            className={rowClassName(isSelected)}
          >
            {checkBox(isSelected)}
            {optionText(option)}
          </p>
        </li>
      )
    }

    return (
      <li key={option.href} {...itemProps}>
        <Link
          href={option.href}
          // 접힌 패널이 열릴 때 뷰포트 자동 RSC prefetch가 네트워크 요청을 일으키지 않게 한다
          prefetch={false}
          {...radioProps}
          aria-current={option.isCurrent && !isEnhanced ? "page" : undefined}
          // 선택 상태를 스타일과 무관하게 드러낸다(단일 선택 계약을 테스트로 잠그기 위함)
          data-selected={isSelected ? "true" : "false"}
          onClick={(event) => onSelect(event, option)}
          className={rowClassName(isSelected)}
        >
          {checkBox(isSelected)}
          {optionText(option)}
        </Link>
      </li>
    )
  }

  return (
    <details
      ref={detailsRef}
      name="directory-filter"
      className="group static"
      // JS 없이도 열리는 게 이 컴포넌트의 계약이라, 사용자가 hydration 전에 칩을 열면
      // 서버·클라이언트 JSX에 없는 open이 실제 DOM에만 존재한다. 의도된 차이이므로
      // 이 요소 한정으로 경고를 끈다(open 값 자체는 그대로 유지된다).
      suppressHydrationWarning
      onToggle={(event) => {
        const self = event.currentTarget as HTMLDetailsElement
        if (!self.open) return
        // details[name] 미지원 브라우저에서도 한 번에 하나만 열리게 강제한다.
        // 이게 있어야 open 속성(=AT의 '확장됨')·화살표·패널 표시가 항상 일치한다 —
        // CSS :has() 안전망만으로는 open이 남아 "펼쳐졌다는데 안 보이는" 상태가 됐다.
        self.parentElement?.querySelectorAll("details").forEach((other) => {
          if (other !== self) other.open = false
        })
      }}
    >
      <summary ref={summaryRef} className={chipSummaryClassName(isChipSelected)}>
        {chipIcon}
        {chipLabel}
        {/* 안전망으로 패널이 가려진 칩은 화살표도 '닫힘'으로 되돌린다 —
            summary 표시와 패널 표시가 어긋나는 상태를 만들지 않는다. */}
        <span
          aria-hidden
          className="text-[10px] leading-none group-open:hidden [details[open]:has(~details[open])_&]:inline"
        >
          ∨
        </span>
        <span
          aria-hidden
          className="hidden text-[10px] leading-none group-open:inline [details[open]:has(~details[open])_&]:hidden"
        >
          ∧
        </span>
      </summary>

      {/* 배경 딤은 승격됐을 때만 존재한다. 포커스 순서·접근성 트리에서는 빼고(닫기는 ✕가 담당),
          포인터로 바깥을 눌러 닫는 용도로만 둔다. */}
      {isDialog ? (
        <button
          type="button"
          aria-hidden
          tabIndex={-1}
          data-testid="filter-sheet-backdrop"
          onClick={() => close()}
          className="fixed inset-0 z-[101] bg-[#26262C]/40"
        />
      ) : null}

      <div
        ref={sheetRef}
        {...(isDialog
          ? {
            role: "dialog",
            "aria-modal": true,
            "aria-labelledby": `${baseId}-title`,
            tabIndex: -1,
          }
          : {})}
        onKeyDown={onSheetKeyDown}
        className={
          isDialog
            // 앱은 min-w-[375px] max-w-[600px] mx-auto 모바일 폭 레이아웃이다(app/[locale]/layout.tsx).
            // 시트만 뷰포트 기준이면 PC에서 혼자 화면 전체로 퍼지므로 컨테이너 폭에 맞춰 가운데 정렬한다
            // (BottomButton·ReviewFilterModal과 같은 관례). 딤은 모달 표준대로 뷰포트 전체를 덮는다.
            ? "fixed inset-x-0 bottom-0 z-[102] mx-auto flex w-full max-w-[600px] max-h-[85vh] flex-col rounded-t-2xl bg-white shadow-[0_-8px_24px_rgba(38,38,44,0.16)] outline-none"
            // 비승격 폴백: 지금까지처럼 칩 줄 아래 전체 폭으로 펼쳐지는 패널
            : "absolute inset-x-0 top-full z-20 mt-2 max-h-[60vh] overflow-y-auto rounded-xl border border-gray-100 bg-white p-3 shadow-[0_8px_24px_rgba(38,38,44,0.12)] [details[open]:has(~details[open])>&]:hidden"
        }
      >
        <div className={isDialog ? "flex shrink-0 items-center justify-between px-4 py-4" : ""}>
          <h2 id={`${baseId}-title`} className="text-lg font-bold text-[#26262C]">
            {title}
            <span className="sr-only"> — {contextLabel}</span>
          </h2>
          {isDialog ? (
            <button type="button" aria-label="닫기" onClick={() => close()} className="p-1 text-2xl leading-none text-[#26262C]">
              ✕
            </button>
          ) : null}
        </div>

        <div className={isDialog ? "flex min-h-0 flex-1 border-t border-[#EAEBEC]" : ""}>
          {withSidebar && isDialog ? (
            <div
              role="tablist"
              aria-orientation="vertical"
              aria-label={`${title} 시도 선택`}
              className="min-h-0 w-2/5 shrink-0 overflow-y-auto border-r border-[#EAEBEC]"
            >
              {buckets.map((bucket, index) => (
                <button
                  key={bucket.label}
                  type="button"
                  role="tab"
                  id={`${baseId}-tab-${index}`}
                  ref={(node) => { tabRefs.current[index] = node }}
                  aria-selected={index === visibleBucket}
                  aria-controls={`${baseId}-panel-${index}`}
                  tabIndex={index === visibleBucket ? 0 : -1}
                  onClick={() => setVisibleBucket(index)}
                  onKeyDown={(event) => onTabKeyDown(event, index)}
                  className={`flex w-full items-center justify-between gap-2 py-3 pl-5 pr-2.5 text-left text-base ${
                    index === visibleBucket
                      ? "bg-[#F4F4F5] font-semibold text-[#1D1E20]"
                      : "font-medium text-[#878A93]"
                  }`}
                >
                  {bucket.label}
                  <span aria-hidden className="text-xs">›</span>
                </button>
              ))}
            </div>
          ) : null}

          <div className={isDialog ? "min-h-0 min-w-0 flex-1 overflow-y-auto" : ""}>
            {buckets.map((bucket, index) => {
              const isBucketVisible = !(withSidebar && isDialog) || index === visibleBucket
              // roving 진입점: 이 통에서 선택된 항목, 없으면 첫 항목
              const selectedInBucket = bucket.options.findIndex(isOptionSelected)
              const rovingIndex = selectedInBucket >= 0 ? selectedInBucket : 0

              return (
                <div
                  key={bucket.label}
                  // 비활성 시도도 DOM에 남는다 — 링크 집합이 초기 HTML에서 줄면 안 된다
                  hidden={withSidebar && isDialog && index !== visibleBucket}
                  {...(withSidebar && isDialog
                    ? { role: "tabpanel", id: `${baseId}-panel-${index}`, "aria-labelledby": `${baseId}-tab-${index}` }
                    // 사이드바가 없을 땐(비승격 폴백·진료과 시트) 묶음을 소제목과 이어 준다
                    : { role: "group", "aria-labelledby": `${baseId}-bucket-${index}` })}
                >
                  {/* 묶음마다 문맥 소제목을 둔다 — 앵커 주변 텍스트가 링크의 의미를 설명해야 한다.
                      승격돼 사이드바가 라벨을 대신할 때만 시각적으로 감추고 헤딩 구조는 남긴다. */}
                  <h3
                    id={`${baseId}-bucket-${index}`}
                    className={withSidebar && isDialog
                      ? "sr-only"
                      : "mb-1 mt-2 px-4 text-xs font-semibold text-[#8B8D94]"}
                  >
                    {bucket.label}
                  </h3>
                  <ul
                    className="flex flex-col"
                    {...(isDialog
                      ? { role: "radiogroup", "aria-labelledby": `${baseId}-bucket-${index}` }
                      : {})}
                  >
                    {bucket.options.map((option, optionIndex) =>
                      optionRow(option, optionIndex, isBucketVisible && optionIndex === rovingIndex))}
                  </ul>
                </div>
              )
            })}
          </div>
        </div>

        {isDialog ? (
          <div className="flex shrink-0 gap-1.5 border-t border-[#EAEBEC] bg-white px-4 py-3">
            <button
              type="button"
              onClick={() => { setSelectedHref(null); setVisibleBucket(currentBucketIndex) }}
              className="w-1/3 rounded border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700"
            >
              초기화
            </button>
            {/* 완료는 앵커가 아니라 버튼이다 — 필터 안의 크롤 가능한 링크 집합을 늘리지 않는다 */}
            <button
              type="button"
              onClick={() => {
                if (selectedHref === null) { close(); return }
                window.location.assign(selectedHref)
              }}
              className="w-2/3 rounded bg-primary-600 py-3 text-sm font-semibold text-white"
            >
              완료
            </button>
          </div>
        ) : null}
      </div>
    </details>
  )
}

export default DirectoryFilterSheet
